"use client";

import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslations, useLocale } from "next-intl";
import { CheckCircle2, ChevronLeft, ChevronRight, Home } from "lucide-react";
import { animate } from "animejs";
import { Link } from "@/i18n/navigation";
import { isRTL, type Locale } from "@/i18n/routing";
import { QUESTIONS } from "@/lib/constants";
import { useQuiz } from "@/hooks";
import { LoveLanguage } from "@/types";
import { useNavigation } from "@/contexts/navigation-context";
import { ButtonLoader } from "@/components/ui/button-loader";

export function QuizClient() {
  const t = useTranslations();
  const locale = useLocale() as Locale;
  const { navigateTo, isNavigating } = useNavigation();
  const rtl = isRTL(locale);
  const cardRef = useRef<HTMLDivElement>(null);

  const { answers, setAnswer, isComplete, currentQuestionIndex } = useQuiz();
  const [questionIndex, setQuestionIndex] = useState(currentQuestionIndex);
  const [direction, setDirection] = useState<"next" | "prev">("next");

  const randomizedOptions = useMemo(
    () => QUESTIONS.map(() => (Math.random() > 0.5 ? 1 : 0)),
    [],
  );

  const currentQuestion = QUESTIONS[questionIndex];
  const progress = ((questionIndex + 1) / QUESTIONS.length) * 100;

  const flip = randomizedOptions[questionIndex] === 1;
  const option1 = flip
    ? currentQuestion.options[1]
    : currentQuestion.options[0];
  const option2 = flip
    ? currentQuestion.options[0]
    : currentQuestion.options[1];

  const currentAnswer = answers[currentQuestion.id];
  const hasAnsweredCurrent = !!currentAnswer;

  useEffect(() => {
    if (cardRef.current) {
      const startX = direction === "next" ? 50 : -50;
      animate(cardRef.current, {
        opacity: [0, 1],
        translateX: rtl ? [-startX, 0] : [startX, 0],
        ease: "out(3)",
        duration: 300,
      });
    }
  }, [questionIndex, direction, rtl]);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("q", String(questionIndex + 1));
    window.history.replaceState({}, "", url.toString());
  }, [questionIndex]);

  const goToNext = () => {
    if (questionIndex < QUESTIONS.length - 1) {
      setDirection("next");
      setQuestionIndex((prev) => prev + 1);
    } else {
      navigateTo("/results");
    }
  };

  const goToPrev = () => {
    if (questionIndex > 0) {
      setDirection("prev");
      setQuestionIndex((prev) => prev - 1);
    }
  };

  const handleSelection = (
    questionId: number,
    selectedLanguage: LoveLanguage,
  ) => {
    setAnswer(questionId, selectedLanguage);
    goToNext();
  };

  const getOptionClasses = (isSelected: boolean) => {
    return isSelected
      ? "option-card selected group relative flex items-center p-5 rounded-2xl text-left rtl:text-right cursor-pointer"
      : "option-card group relative flex items-center p-5 rounded-2xl text-left rtl:text-right cursor-pointer";
  };

  const getIconClasses = (isSelected: boolean) => {
    return isSelected
      ? "absolute right-4 rtl:right-auto rtl:left-4 opacity-100 text-primary transition-opacity"
      : "absolute right-4 rtl:right-auto rtl:left-4 opacity-0 group-hover:opacity-100 transition-opacity text-primary";
  };

  return (
    <div
      className="w-full max-w-2xl mx-auto px-4 py-8 flex flex-col min-h-[70vh]"
      data-testid="quiz-component"
    >
      <h1 className="sr-only">{t("quiz_page_title")}</h1>
      <div className="sticky top-16 z-40 bg-background/80 backdrop-blur-md pt-5 pb-5 mb-8 -mx-4 px-4 sm:mx-0 sm:px-0 transition-all border-b border-border/50">
        <div className="flex justify-between items-center mb-4 px-1">
          <span
            className="text-sm font-medium text-muted-foreground"
            data-testid="quiz-progress-text"
          >
            {t("question_progress", {
              current: questionIndex + 1,
              total: QUESTIONS.length,
            })}
          </span>
          <span className="text-lg font-display font-semibold text-primary">
            {Math.round(progress)}%
          </span>
        </div>
        <div
          className="h-2 w-full bg-muted rounded-full overflow-hidden"
          dir="ltr"
          data-testid="quiz-progress-bar"
        >
          <div
            className="h-full progress-bar-fill rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center relative w-full mt-2">
        <h2 className="font-display text-2xl md:text-3xl font-medium text-center mb-8 text-foreground">
          {t("question_prompt")}
        </h2>

        <div
          ref={cardRef}
          className="w-full bg-card border border-border card-elevated rounded-3xl p-6 md:p-8 mx-auto flex flex-col gap-5"
          data-testid="quiz-question-card"
        >
          <div className="grid gap-4" data-testid="quiz-options-grid">
            <button
              onClick={() => handleSelection(currentQuestion.id, option1.type)}
              className={getOptionClasses(currentAnswer === option1.type)}
              data-testid={`quiz-option-${currentQuestion.id}-${option1.type}`}
            >
              <div
                className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center mr-4 rtl:mr-0 rtl:ml-4 transition-all ${
                  currentAnswer === option1.type
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                }`}
              >
                <span className="text-sm font-bold">A</span>
              </div>
              <span className="flex-1 text-base md:text-lg text-foreground leading-relaxed">
                {t(option1.key)}
              </span>
              <div className={getIconClasses(currentAnswer === option1.type)}>
                <CheckCircle2 className="w-6 h-6" />
              </div>
            </button>

            <button
              onClick={() => handleSelection(currentQuestion.id, option2.type)}
              className={getOptionClasses(currentAnswer === option2.type)}
              data-testid={`quiz-option-${currentQuestion.id}-${option2.type}`}
            >
              <div
                className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center mr-4 rtl:mr-0 rtl:ml-4 transition-all ${
                  currentAnswer === option2.type
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                }`}
              >
                <span className="text-sm font-bold">B</span>
              </div>
              <span className="flex-1 text-base md:text-lg text-foreground leading-relaxed">
                {t(option2.key)}
              </span>
              <div className={getIconClasses(currentAnswer === option2.type)}>
                <CheckCircle2 className="w-6 h-6" />
              </div>
            </button>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-2">
            {t("select_option")}
          </p>
        </div>

        <div className="w-full flex items-center justify-between mt-10 px-2 pb-8">
          {questionIndex > 0 ? (
            <button
              onClick={goToPrev}
              className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground px-5 py-3 rounded-xl hover:bg-secondary transition-all cursor-pointer"
              data-testid="quiz-prev"
            >
              <ChevronLeft className={`w-5 h-5 ${rtl ? "rotate-180" : ""}`} />
              <span className="hidden sm:inline">{t("btn_back")}</span>
            </button>
          ) : (
            <div className="w-24" />
          )}

          <Link
            href="/"
            className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground px-5 py-3 rounded-xl hover:bg-secondary transition-all"
            data-testid="quiz-home"
          >
            <Home className="w-5 h-5" />
            <span className="hidden sm:inline">{t("btn_home")}</span>
          </Link>

          <button
            onClick={goToNext}
            disabled={
              (!hasAnsweredCurrent && questionIndex < QUESTIONS.length - 1) ||
              isNavigating
            }
            className="btn-primary flex items-center gap-2 text-sm font-semibold text-primary-foreground px-6 py-3 rounded-xl transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer disabled:transform-none"
            data-testid="quiz-next"
          >
            {isNavigating ? (
              <ButtonLoader size="sm" />
            ) : (
              <>
                <span className="hidden sm:inline">{t("btn_next")}</span>
                <ChevronRight
                  className={`w-5 h-5 ${rtl ? "rotate-180" : ""}`}
                />
              </>
            )}
          </button>
        </div>

        {(questionIndex === 9 || questionIndex === 19) && (
          <div className="w-full max-w-2xl mt-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
            <div className="flex gap-3">
              <span className="text-lg">💡</span>
              <p className="text-xs text-amber-950 dark:text-amber-100 leading-relaxed">
                {t("did_you_know")}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
